import NiceModal from '@ebay/nice-modal-react';
import PropTypes from 'prop-types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Chips from 'components/field/Chips';
import CopyComponent from 'components/field/CopyComponent';
import EnumSelect from 'components/field/EnumSelect';
import PasswordInput from 'components/field/PasswordInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Checkbox from 'components/global/Checkbox';
import Typography from 'components/global/Typography';

import useDebounce from 'hooks/useDebounce';
import { usePrompt } from 'hooks/usePrompt';
import useUpdateEffect from 'hooks/useUpdateEffect';

import AdvanceSettings from 'pages/SalesChannels/AdvanceSettings';
import DeveloperSettings from 'pages/SalesChannels/DeveloperSettings';
import ActivateStore from 'pages/SalesChannels/Fields/ActivateStore';
import useSallaSync from 'pages/SalesChannels/useSallaSync';

import { sallaAxios } from 'services/axios';
import { ENUMS } from 'services/consts';

import { PLATFORM } from 'utils/constants';

import * as S from '../../SalesChannel.styled';
import useAuthorize from '../useAuthorize';
import SallaConnectModal from './SallaConnectModal';

const BaseSallaForm = ({
	advanceSettingsFields = [],
	developerSettingsFields = [],
	showUsername = false,
	showStatuses = false,
	showCredentials = false,
	showCallbackUrl = false,
	showEmail = false,
	showPassword = false,
	showBaseUrl = false,
	showCredentialsApproval = false,
	copyStoreName = false,
	copyBaseUrl = false,
	showDcName = false,
	showOTP = false
}) => {
	const { t } = useTranslation();
	const { authorize, authorizeLoading } = useAuthorize();
	const [countdown, setCountdown] = useState(60);
	const [isActive, setIsActive] = useState(false);
	const [isShowOTP, setIsShowOTP] = useState(false);
	const [sallaCouriers, setSallaCouriers] = useState([]);
	const sallaStores = useSelector((state) => state.salla.sallaStores);
	const { logEcommerceRequest } = useSallaSync();
	const {
		watch,
		setValue,
		trigger,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading, errors }
	} = useFormContext();

	const isLoading = isFormLoading || isSubmitting;

	const { salesChannel } = useSelector((state) => state.salesChannel);

	const isEditMode = watch('formMode') === 'edit';

	const username = useDebounce(watch('username'));
	const secret = useDebounce(watch('secret'));
	const courierNames = useDebounce(watch('statuses'));

	const isCredentialsValid = useMemo(() => Boolean(username?.trim()) && Boolean(secret?.trim()), [username, secret]);

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const handleSelectSallaStore = useCallback((value) => {
		setValue('merchantId', value?.id, { shouldValidate: true, shouldDirty: true });
		setValue('desktopAccessToken', value?.token, {
			shouldValidate: true,
			shouldDirty: true
		});
		setValue('desktopRefreshToken', value?.refresh_token, {
			shouldValidate: true,
			shouldDirty: true
		});
		setValue('desktopTokenExpireDate', value?.expire_at, {
			shouldValidate: true,
			shouldDirty: true
		});
	}, []);

	const authorizeOTP = useCallback(async () => {
		const validationResult = await trigger(['username', 'secret']);
		if (!validationResult) return;
		const res = await authorize({
			queryParams: {
				code: `${watch('username')}:${watch('secret')}`
			}
		});
		if (res.success) {
			setCountdown(60);
			setIsActive(true);
			setIsShowOTP(true);
		}
	}, [isCredentialsValid]);

	const isSallaDesktop = useMemo(() => {
		return (
			(salesChannel?.slug === 'salla-automation' && import.meta.env.VITE_PLATFORM === PLATFORM.desktop) ||
			salesChannel?.slug === 'salla-desktop'
		);
	}, [salesChannel]);

	useEffect(() => {
		if (showCredentialsApproval) {
			setValue('credentialsApproval', !!watch('credentialsApprovalDate'));
		}
	}, [isFormLoading, showCredentialsApproval]);

	const selectedMerchant = useMemo(() => {
		return sallaStores.find((store) => store.id === Number(watch('merchantId')));
	}, [sallaStores, watch('merchantId')]);

	useEffect(() => {
		let intervalId;
		if (isActive && countdown > 0) {
			intervalId = setInterval(() => {
				setCountdown((prev) => prev - 1);
			}, 1000);
		} else if (countdown === 0) {
			setIsActive(false);
			setIsShowOTP(false);
		}
		return () => clearInterval(intervalId);
	}, [isActive, countdown]);

	useEffect(() => {
		if (selectedMerchant?.token) {
			sallaAxios
				.get('/shipping/companies', {
					headers: {
						Authorization: `Bearer ${selectedMerchant.token}`
					}
				})
				.then((res) => {
					setSallaCouriers(res.data);
					logEcommerceRequest({
						isFailure: false,
						url: 'https://api.salla.dev/admin/v2/shipping/companies',
						request: JSON.stringify({ token: selectedMerchant.token }),
						response: JSON.stringify(res.data),
						action: 'getSallaCouriers'
					});
				})
				.catch((error) => {
					logEcommerceRequest({
						isFailure: true,
						url: 'https://api.salla.dev/admin/v2/shipping/companies',
						request: JSON.stringify({ token: selectedMerchant.token }),
						response: JSON.stringify(error.response?.data || error.message),
						action: 'getSallaCouriers'
					});
				});
		}
	}, [selectedMerchant]);

	useEffect(() => {
		if (courierNames?.length && sallaCouriers?.length) {
			const matchingCourierIds = [];
			if (Array.isArray(courierNames) && Array.isArray(sallaCouriers)) {
				courierNames.forEach((statusName) => {
					const foundCourier = sallaCouriers.find((courier) => courier.name === statusName);
					if (foundCourier) {
						matchingCourierIds.push(foundCourier.id);
					}
				});
			}
			setValue('courierIds', matchingCourierIds, { shouldValidate: true, shouldDirty: true });
		}
	}, [courierNames, sallaCouriers]);

	useUpdateEffect(() => {
		if (sallaStores?.length) {
			handleSelectSallaStore(sallaStores[sallaStores.length - 1]);
		}
	}, [sallaStores]);

	return (
		<>
			<S.StoreAccordion header="storeDetails" defaultVisible>
				<FormItem label="sc.storeName" isLoading={isLoading} required>
					{copyStoreName ? (
						<CopyComponent
							value={watch('storeName') || t('saveTheFormToGetStoreName')}
							buttonDisabled={!watch('storeName')}
						/>
					) : (
						<StringInput name="storeName" />
					)}
				</FormItem>

				{showDcName && (
					<FormItem
						label="sallaAutomationForm.dcName"
						toolTip="sallaAutomationForm.dcNameTooltip"
						isLoading={isLoading}
						required>
						<StringInput name="apiUrl" disabled={isEditMode} />
					</FormItem>
				)}

				{import.meta.env.VITE_PLATFORM === PLATFORM.web && (
					<>
						{showEmail && (
							<FormItem label="sallaAutomationForm.email" isLoading={isLoading} required>
								<StringInput name="username" />
							</FormItem>
						)}

						{showPassword && (
							<FormItem label="sallaAutomationForm.password" isLoading={isLoading} required>
								<PasswordInput name="secret" disableIcon={isEditMode} autoComplete="one-time-code" />
								<Typography className="mt-2">sallaAutomationForm.passwordDescription</Typography>
							</FormItem>
						)}
						{showOTP && (
							<>
								<FormItem label="sallaAutomationForm.OTPCode" isLoading={isLoading} required>
									<PasswordInput
										name="password"
										disableIcon={isEditMode}
										autoComplete="one-time-code"
										maxLength={4}
										disabled={!isShowOTP || !isActive}
									/>
									{isActive ? (
										<Typography className="mt-2">
											<Trans
												i18nKey="sallaAutomationForm.OTPCodeCountDown"
												values={{
													countdown
												}}
											/>
										</Typography>
									) : (
										<Typography className="mt-2">sallaAutomationForm.OTPCodeDescription</Typography>
									)}
								</FormItem>
								<S.OTPWrapper>
									<Button
										label="sendOTP"
										variant="blue"
										onClick={authorizeOTP}
										size="large"
										loading={authorizeLoading}
										disabled={isActive}
									/>
								</S.OTPWrapper>
							</>
						)}
					</>
				)}

				{showCredentials && (
					<>
						<FormItem label="sallaPrivateApp.token" isLoading={isLoading} required>
							<StringInput name="token" />
						</FormItem>
						<FormItem label="sallaPrivateApp.productUrl" isLoading={isLoading} required>
							<StringInput name="productUrl" />
						</FormItem>
					</>
				)}

				{showUsername && (
					<FormItem label="storeID" isLoading={isLoading}>
						<CopyComponent value={watch('username')} />
					</FormItem>
				)}
				{showCallbackUrl && (
					<FormItem label="callbackUrl" isLoading={isLoading}>
						<CopyComponent
							value={watch('apiUrl') || t('saveTheFormToGetCallbackUrl')}
							buttonDisabled={!watch('apiUrl')}
						/>
					</FormItem>
				)}

				{copyBaseUrl && (
					<FormItem label={`${salesChannel?.slug}.baseUrl`} isLoading={isLoading}>
						<CopyComponent value={watch('baseUrl')} />
					</FormItem>
				)}

				{showBaseUrl && (
					<FormItem label={`${salesChannel?.slug}.baseUrl`} isLoading={isLoading}>
						<StringInput name="baseUrl" />
					</FormItem>
				)}

				{import.meta.env.VITE_PLATFORM === PLATFORM.web && showCredentialsApproval && (
					<ConcentForm isLoading={isLoading} errors={errors} watch={watch} setValue={setValue} />
				)}

				{isSallaDesktop && (
					<FormItem label="sallaStores" toolTip="merchantId.tooltip" isLoading={isLoading} required>
						<EnumSelect
							data={sallaStores}
							value={selectedMerchant}
							setValue={handleSelectSallaStore}
							renderSelectionValue={(value) => (
								<Typography fontWeight="500">{value?.name || value?.username}</Typography>
							)}
							renderOption={(value) => (
								<Typography fontWeight="500">{value?.name || value?.username}</Typography>
							)}
							name="merchantId"
							firstItem={{
								action: () => NiceModal.show(SallaConnectModal),
								label: <Typography color="var(--blue)">addNewConnection</Typography>
							}}
							searchable={false}
						/>
					</FormItem>
				)}
				{showStatuses && (
					<FormItem label="sallaWebhookForm.statuses" isLoading={isLoading}>
						<Chips
							name="statuses"
							value={watch('statuses')}
							setValue={(value) => {
								setValue(`statuses`, value ?? null, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				)}
				{isSallaDesktop && (
					<FormItem label="syncInterval" toolTip="syncInterval.tooltip" isLoading={isLoading}>
						<EnumSelect
							name="syncInterval"
							enumName={ENUMS.SALLA_SYNC_INTERVAL}
							placeholder="syncInterval"
							setValue={(value) => {
								setValue('syncInterval', Number(value), {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('syncInterval')}
						/>
					</FormItem>
				)}
				<ActivateStore isLoading={isLoading} />
				{import.meta.env.VITE_PLATFORM === PLATFORM.desktop && showCredentialsApproval && (
					<ConcentForm isLoading={isLoading} errors={errors} watch={watch} setValue={setValue} />
				)}
			</S.StoreAccordion>

			<AdvanceSettings fields={advanceSettingsFields} enumName={ENUMS.SALLA_CRENDENTIALS_STATUSES} />
			<DeveloperSettings
				fields={[
					'password',
					'secret',
					'codString',
					'ignoreNonCodOnPending',
					'commentKeys',
					'TrackingUrlType',
					...developerSettingsFields
				]}
			/>
		</>
	);
};

const ConcentForm = ({ isLoading, errors, watch, setValue }) => (
	<>
		<FormItem isLoading={isLoading}>
			<S.CheckboxContainer hasError={errors?.credentialsApproval}>
				<Checkbox
					checked={!!watch('credentialsApproval')}
					onChange={(e) =>
						setValue('credentialsApproval', e?.target?.checked, {
							shouldValidate: true,
							shouldDirty: true
						})
					}
					error={errors?.credentialsApproval?.message}
				/>
				<Typography
					color="var(--content-3)"
					className="credentials-approval"
					noWrap
					onClick={() =>
						setValue('credentialsApproval', !watch('credentialsApproval'), {
							shouldValidate: true,
							shouldDirty: true
						})
					}>
					<Trans i18nKey="salla.credentialsApproval" />
				</Typography>
			</S.CheckboxContainer>
		</FormItem>
	</>
);

BaseSallaForm.propTypes = {
	advanceSettingsFields: PropTypes.arrayOf(PropTypes.string),
	developerSettingsFields: PropTypes.arrayOf(PropTypes.string),
	showUsername: PropTypes.bool,
	showStatuses: PropTypes.bool,
	showWarehouse: PropTypes.bool,
	showCredentials: PropTypes.bool,
	copyStoreName: PropTypes.bool,
	showCallbackUrl: PropTypes.bool,
	copyBaseUrl: PropTypes.bool
};

export default BaseSallaForm;
